// 치과 「원장 호출」 공용 상수·도우미 (2026-10-08, 명세 docs/명세_원장호출_웹_2026-10-08.md)
//  - 저장 데이터 = 체어 번호·상황 코드·시각·응답만(환자·직원 이름 0, 자유 입력 0)
//  - 의료기기 아님(알림만). "표준 타이머"·"마취 지속 판단" 표현 금지 — 타이머 기능 없음
//  - 격자 화면은 라이트 고정·상황색 고정(명세 §2)

// 상황 4칸 — 키는 서버 검증 값과 같다(ready|anesthesia|check|urgent). 색은 칸 위치(열) 고정
export const SITUATION_KEYS = Object.freeze(['ready', 'anesthesia', 'check', 'urgent']);
export const SITUATION_COLORS = Object.freeze({ ready: '#1A8F6F', anesthesia: '#3B82F6', check: '#D97706', urgent: '#DC2626' });
export const URGENT_ALT_COLOR = '#C2410C';   // 설정에서 고를 수 있는 「급함」 대체색(진한 주황)

// 상황 문구 사전 목록 10개 — 4칸 각각 이 목록에서만 고른다(자유 입력 없음). 저장값 = 키(계약 §3-1), 표시 = 문구
export const PHRASES = Object.freeze([
    { key: 'ready', text: '준비 완료' },
    { key: 'anesthesia', text: '마취 완료' },
    { key: 'check', text: '확인' },
    { key: 'urgent', text: '급함' },
    { key: 'check_request', text: '체크 요청' },
    { key: 'impression_done', text: '인상 완료' },
    { key: 'try_in', text: '보철 시적' },
    { key: 'consult_request', text: '상담 요청' },
    { key: 'finish_check', text: '마무리 확인' },
    { key: 'photo_done', text: '촬영 완료' },
]);
export const PHRASE_KEYS = Object.freeze(PHRASES.map(p => p.key));
export const phraseText = (key) => (PHRASES.find(p => p.key === key) || {}).text || '';
export const DEFAULT_LABELS = Object.freeze({ ready: 'ready', anesthesia: 'anesthesia', check: 'check', urgent: 'urgent' });

// 원장 응답 3종 — 키는 서버 검증 값(coming|in2|in5). 선택지 세트 'std'(가는 중/2분/5분) | 'min135'(1분/3분/5분)
//   min135 은 같은 3키를 1·3·5분으로 읽는다: coming=1분, in2=3분, in5=5분 (서버 키 3종 유지)
export const ACK_KEYS = Object.freeze(['coming', 'in2', 'in5']);
export function ackOptions(set) {
    if (set === 'min135') return [{ key: 'coming', label: '1분', min: 1 }, { key: 'in2', label: '3분', min: 3 }, { key: 'in5', label: '5분', min: 5 }];
    return [{ key: 'coming', label: '가는 중', min: 0 }, { key: 'in2', label: '2분', min: 2 }, { key: 'in5', label: '5분', min: 5 }];
}
// 응답의 분(0 = 「원장 오는 중」 — 남은 시간 없음)
export function ackMinutes(set, type) {
    const o = ackOptions(set).find(x => x.key === type);
    return o ? o.min : 0;
}

export const DEFAULT_SETTINGS = Object.freeze({
    chairs: 4,                 // 2~12
    labels: DEFAULT_LABELS,    // 상황 4칸 문구(사전 목록 중)
    ackSet: 'std',             // 'std' | 'min135'
    soundUpSec: 30,            // 미응답 → 깜빡임·원장 쪽 소리↑ (15·30·60)
    deskShowSec: 60,           // 미응답 → 데스크 띠 펼침 (30·60·120)
    urgentColor: 'red',        // 'red' | 'orange'
});
export const SOUND_UP_CHOICES = Object.freeze([15, 30, 60]);
export const DESK_SHOW_CHOICES = Object.freeze([30, 60, 120]);
export const CHAIRS_MIN = 2;
export const CHAIRS_MAX = 12;

const clampInt = (v, lo, hi, dft) => {
    const n = Math.round(Number(v));
    return Number.isFinite(n) ? Math.min(hi, Math.max(lo, n)) : dft;
};

// 서버 문서(없을 수 있음) → 화면에서 쓰는 설정. 모르는 값은 기본값으로
export function normalizeSettings(raw) {
    const r = raw && typeof raw === 'object' ? raw : {};
    let labels = {};
    SITUATION_KEYS.forEach(k => {
        const v = r.labels && r.labels[k];
        labels[k] = PHRASE_KEYS.includes(v) ? v : DEFAULT_LABELS[k];
    });
    // 4칸 문구는 서로 달라야 한다(계약 §3) — 겹치면 기본 문구로
    if (new Set(SITUATION_KEYS.map(k => labels[k])).size !== SITUATION_KEYS.length) labels = { ...DEFAULT_LABELS };
    const soundUpSec = SOUND_UP_CHOICES.includes(Number(r.soundUpSec)) ? Number(r.soundUpSec) : DEFAULT_SETTINGS.soundUpSec;
    let deskShowSec = DESK_SHOW_CHOICES.includes(Number(r.deskShowSec)) ? Number(r.deskShowSec) : DEFAULT_SETTINGS.deskShowSec;
    if (deskShowSec < soundUpSec) deskShowSec = DESK_SHOW_CHOICES.find(x => x >= soundUpSec) || DESK_SHOW_CHOICES[DESK_SHOW_CHOICES.length - 1];
    return {
        chairs: clampInt(r.chairs, CHAIRS_MIN, CHAIRS_MAX, DEFAULT_SETTINGS.chairs),
        labels,
        ackSet: r.ackSet === 'min135' ? 'min135' : 'std',
        soundUpSec,
        deskShowSec,
        urgentColor: r.urgentColor === 'orange' ? 'orange' : 'red',
    };
}

export function situationColor(key, settings) {
    if (key === 'urgent' && settings?.urgentColor === 'orange') return URGENT_ALT_COLOR;
    return SITUATION_COLORS[key] || '#64748b';
}
export function situationLabel(key, settings) {
    return phraseText((settings?.labels && settings.labels[key]) || DEFAULT_LABELS[key]) || phraseText(DEFAULT_LABELS[key]) || '';
}

// Firestore Timestamp | Date | number | ISO → ms (없으면 NaN)
export function toMs(v) {
    if (!v) return NaN;
    if (typeof v.toMillis === 'function') return v.toMillis();
    if (typeof v.seconds === 'number') return v.seconds * 1000 + Math.floor((v.nanoseconds || 0) / 1e6);
    if (v instanceof Date) return v.getTime();
    if (typeof v === 'number') return v;
    const n = Date.parse(v);
    return Number.isFinite(n) ? n : NaN;
}

// m:ss (음수는 0)
export function fmtMSS(ms) {
    const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
    return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

// 응답 뒤 남은 시간(ms). ackMin 0(「가는 중」) 또는 응답 없음 = null. 저장된 ackMin 을 쓴다(설정이 바뀌어도 그대로 — 계약 §5)
export function ackRemainMs(call, nowMs) {
    if (!call?.ack?.type) return null;
    const min = Number(call.ack.min) || 0;
    if (!min) return null;
    const at = toMs(call.ack.at);
    if (!Number.isFinite(at)) return null;
    return at + min * 60000 - nowMs;
}
// 응답 라벨 — 저장된 ackMin 기준(0 = 원장 오는 중)
export function ackLabel(call) {
    const min = Number(call?.ack?.min) || 0;
    return min ? `${min}분` : '원장 오는 중';
}

// 원장 보기 정렬 — 급함 > 마취(경과 긴 순) > 그 외 대기 긴 순 (명세 §3)
export function sortForDoctor(calls) {
    const rank = (c) => (c.situation === 'urgent' ? 0 : c.situation === 'anesthesia' ? 1 : 2);
    return [...calls].sort((a, b) => {
        const r = rank(a) - rank(b);
        if (r) return r;
        return (toMs(a.createdAt) || 0) - (toMs(b.createdAt) || 0);   // 먼저 온 것 = 경과 긴 것
    });
}

// 격자 칸 크기 — 4대 이하 104px, 5~8대 88px, 9대 이상 2열(88px). 터치 최소 48px
export function gridLayout(chairs) {
    if (chairs <= 4) return { cell: 104, cols: 1 };
    if (chairs <= 8) return { cell: 88, cols: 1 };
    return { cell: 88, cols: 2 };
}

// 브라우저 알림·소리용 짧은 문구(환자 정보 없음)
export function callText(call, settings) {
    return `${call.chairNo}번 체어 · ${situationLabel(call.situation, settings)}`;
}

// 서버 오류 → 화면 문구
export function doctorCallError(e, fallback) {
    const code = String(e?.code || '');
    if (e?.details?.code === 'feature-off') return '원장 호출 기능이 아직 열리지 않았습니다.';
    if (code.includes('permission-denied')) return '권한이 없습니다(같은 치과 계정만 쓸 수 있습니다). 서버 준비 전이면 잠시 후 다시 시도해 주세요.';
    if (code.includes('unavailable') || code.includes('deadline')) return '서버에 연결하지 못했습니다. 인터넷 연결을 확인해 주세요.';
    return fallback;
}
